import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";

export interface SelectOption {
  label: string;
  /** Optional image (e.g. a flag) shown before the label in single-select */
  icon?: string;
  value: string | number;
}

export interface CommonMultiSelectProps {
  options: SelectOption[];
  value: Array<string | number>;
  onChange: (values: Array<string | number>) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  fullWidth?: boolean;
  size?: "small" | "medium";
  onClose?: () => void;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const CommonMultiSelect: React.FC<CommonMultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select options",
  disabled = false,
  loading = false,
  label,
  fullWidth = true,
  size = "small",
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  // Filter options based on search term (excluding "all" option from filtering if search term exists)
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(lowerSearch)
    );
  }, [options, searchTerm]);

  // Compute select all state
  const selectAllState = useMemo(() => {
    if (filteredOptions.length === 0) return { checked: false, indeterminate: false };

    const filteredValues = filteredOptions.map((opt) => opt.value);
    const selectedFilteredCount = filteredValues.filter((val) =>
      value.includes(val)
    ).length;

    if (value.includes("all") || (options.length > 0 && selectedFilteredCount === filteredOptions.length)) {
      return { checked: true, indeterminate: false };
    } else if (selectedFilteredCount === 0) {
      return { checked: false, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  }, [filteredOptions, options.length, value]);

  // Handle select all toggle
  const handleSelectAll = useCallback(() => {
    const filteredValues = filteredOptions.map((opt) => opt.value);

    if (selectAllState.checked) {
      // Deselect all
      onChange([]);
    } else {
      // If "all" exists in options, select ["all"], otherwise select all values
      const hasAllOption = options.some((opt) => opt.value === "all");
      if (hasAllOption) {
        onChange(["all"]);
      } else {
        const newValue = Array.from(new Set([...value, ...filteredValues]));
        onChange(newValue);
      }
    }
  }, [filteredOptions, options, selectAllState.checked, value, onChange]);

  // Handle individual option toggle
  const handleChange = useCallback(
    (event: SelectChangeEvent<Array<string | number>>) => {
      const selectedValues = event.target.value as Array<string | number>;
      onChange(selectedValues);
    },
    [onChange]
  );

  // Handle search input change
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    []
  );

  // Clear search on close
  const handleClose = useCallback(() => {
    setSearchTerm("");
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Render selected value
  const renderValue = useCallback(
    (selected: Array<string | number>) => {
      if (!selected || selected.length === 0) {
        return <span style={{ color: "#bfbfbf" }}>{placeholder}</span>;
      }

      if (selected.includes("all")) {
        return "All";
      }

      if (options.length > 0 && selected.length === options.length) {
        return "All selected";
      }

      if (selected.length <= 2) {
        return selected
          .map((val) => options.find((opt) => opt.value === val)?.label)
          .filter(Boolean)
          .join(", ");
      }

      return `${selected.length} selected`;
    },
    [options, placeholder]
  );

  const selectHeight = size === "small" ? "32px" : "40px";

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      disabled={disabled || loading}
      sx={{ m: 0 }}
    >
      {label && <InputLabel sx={{ display: "none" }}>{label}</InputLabel>}
      <Select
        multiple
        displayEmpty
        value={value}
        onChange={handleChange}
        onClose={handleClose}
        renderValue={renderValue}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: ITEM_HEIGHT * 8 + ITEM_PADDING_TOP,
              width: 250,
            },
          },
          autoFocus: false,
        }}
        sx={{
          borderRadius: "6px",
          height: selectHeight,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--app-border)",
            borderRadius: "6px",
            "& legend": {
              display: "none",
            },
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#ba2156",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#ba2156",
            borderWidth: "1px",
          },
          "& .MuiSelect-select": {
            padding: "4px 11px",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            height: selectHeight,
            boxSizing: "border-box",
          },
        }}
      >
        {/* Search Input */}
        <MenuItem
          disableRipple
          onKeyDown={(e) => e.stopPropagation()}
          sx={{
            "&:hover": { backgroundColor: "transparent" },
            cursor: "default",
            position: "sticky",
            top: 0,
            backgroundColor: "background.paper",
            zIndex: 1,
            borderBottom: "1px solid var(--app-border-light)",
          }}
        >
          <TextField
            size="small"
            autoFocus
            placeholder="Search..."
            fullWidth
            value={searchTerm}
            onChange={handleSearchChange}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: size === "small" ? "0.875rem" : "1rem",
              },
            }}
          />
        </MenuItem>

        {/* Select All Option */}
        {filteredOptions.length > 0 && !options.some((opt) => opt.value === "all") && (
          <MenuItem
            disableRipple
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSelectAll();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                handleSelectAll();
              }
            }}
            sx={{
              position: "sticky",
              top: 56,
              backgroundColor: "background.paper",
              zIndex: 1,
              borderBottom: "1px solid var(--app-border-light)",
              fontWeight: 600,
              "&:hover": { backgroundColor: "action.hover" },
              cursor: "pointer",
            }}
          >
            <Checkbox
              checked={selectAllState.checked}
              indeterminate={selectAllState.indeterminate}
              sx={{ pointerEvents: "none" }}
            />
            <ListItemText primary="Select All" />
          </MenuItem>
        )}

        {/* Options List */}
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={value.includes(option.value)} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <ListItemText
              primary="No options found"
              sx={{ textAlign: "center", color: "#999" }}
            />
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default CommonMultiSelect;
